import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faFilter } from "@fortawesome/free-solid-svg-icons";
import Cookies from "universal-cookie";
import { Layout } from "../../../../containers/app/layout";
import { DepartmentWrapper, FiltersCard, DepartmentTableCard } from "./styled";
import { Label, P } from "../../../../components/typography/styled";
import { getDepartments } from "../../../../utils/apis/department/getDepartments";
import { deleteDepartmentService } from "../../../../utils/apis/department/deleteDepartment";
import { SuccessModal } from "../../../../containers/app/modals/successmodal";
import { DepartmentBulkUploadModal } from "../../../../containers/app/modals/departmentbulkuploadmodal";
import { AddDepartmentModal } from "../../../../containers/app/modals/adddepartmentmodal";
import { getBulkDepartmentUploadTemplate } from "../../../../utils/apis/department/bulkUploadTemplate";
import { Context } from "../../../../context";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseButton } from "../../../../components/button/styled";

export const Department = () => {
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");
  const COMPANY_ID = cookies.get("COMPANY_ID");

  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const {
    isDepartmentBulkUploadModalOpen,
    setIsDepartmentBulkUploadModalOpen,
    setIsAddDepartmentModalOpen,
  } = useContext(Context);

  const PAGE_SIZE = 10;

  const [departments, setDepartments] = useState([]);
  const [activeDepartmentId, setActiveDepartmentId] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [flag, setFlag] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [alphabetFilter, setAlphabetFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [bulkImportAnchorElement, setBulkImportAnchorElement] = useState(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await getDepartments(TOKEN, COMPANY_ID);
        setDepartments(response.data || []);
      } catch (fetchError) {
        console.error(fetchError);
      }
    };

    fetchDepartments();
  }, [TOKEN, COMPANY_ID, activeDepartmentId, isDepartmentBulkUploadModalOpen]);

  const handleDropDownClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveDepartmentId(null);
    }
  };

  useEffect(() => {
    if (activeDepartmentId !== null) {
      document.addEventListener("mousedown", handleDropDownClickOutside);
    } else {
      document.removeEventListener("mousedown", handleDropDownClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleDropDownClickOutside);
    };
  }, [activeDepartmentId]);

  const handleCloseSuccessModal = () => {
    setFlag(null);
    setIsSuccessModalOpen(false);
  };

  const handlePersistModal = () => {
    setIsSuccessModalOpen(true);
  };

  const handleAddDepartmentButtonClick = (e) => {
    e.stopPropagation();
    navigateToAddNewDepartment(e);
  };

  const handleBulkImportButtonClick = (e) => {
    e.stopPropagation();
    setBulkImportAnchorElement(e.currentTarget);
    setIsAddDepartmentModalOpen(true);
  };

  const handleDownloadBulkDepartmentTemplate = async () => {
    try {
      const blob = await getBulkDepartmentUploadTemplate(TOKEN);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "department-upload-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download template:", error);
    }
  };

  const handleActionItemClick = async (e, action) => {
    e.stopPropagation();
    switch (action) {
      case "bulk-upload":
        setIsDepartmentBulkUploadModalOpen(true);
        break;
      case "download-template":
        await handleDownloadBulkDepartmentTemplate();
        break;
      default:
        return;
    }
    setIsAddDepartmentModalOpen(false);
  };

  const navigateToAddNewDepartment = (e) => {
    e.preventDefault();
    navigate("/adddepartment");
  };

  const deleteDepartment = async (departmentId) => {
    try {
      const response = await deleteDepartmentService(
        TOKEN,
        COMPANY_ID,
        departmentId,
      );
      if (response.status === "Success") {
        setFlag("delete");
        setIsSuccessModalOpen(true);
      } else {
        setError("Delete department operation failed. Please try again.");
        console.error("Delete department operation failed. Please try again.");
      }
    } catch (deleteError) {
      setError("Delete department operation failed. Please try again.");
      console.error(deleteError);
    }
  };

  const handleDepartmentAction = async (e, departmentId, action) => {
    e.stopPropagation();

    switch (action) {
      case "edit":
        navigate(`/departments/${departmentId}`);
        break;
      case "delete":
        await deleteDepartment(departmentId);
        break;
      default:
        break;
    }

    setActiveDepartmentId(null);
  };

  const normalizedDepartments = [...departments].sort(
    (firstDepartment, secondDepartment) =>
      (firstDepartment?.name || "").localeCompare(secondDepartment?.name || ""),
  );

  const filteredDepartments = normalizedDepartments.filter((department) => {
    const departmentName = department?.name || "";
    const normalizedName = departmentName.toLowerCase();
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const firstCharacter = normalizedName.charAt(0);

    const matchesSearch =
      normalizedSearchTerm.length === 0 ||
      normalizedName.includes(normalizedSearchTerm);

    const matchesAlphabet =
      alphabetFilter === "all" ||
      (alphabetFilter === "a-m" &&
        firstCharacter >= "a" &&
        firstCharacter <= "m") ||
      (alphabetFilter === "n-z" &&
        firstCharacter >= "n" &&
        firstCharacter <= "z");

    return matchesSearch && matchesAlphabet;
  });

  const totalFilteredDepartments = filteredDepartments.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalFilteredDepartments / PAGE_SIZE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedDepartments = filteredDepartments.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const visiblePageNumbers = (() => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  })();

  const rangeStart =
    totalFilteredDepartments === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalFilteredDepartments);
  return (
    <Layout
      id={"departments"}
      title={"Departments"}
      location={"departments"}
      callToAction={"Add Department"}
      handleCallToActionClick={handleAddDepartmentButtonClick}
      bulkCallToAction={"Bulk Import"}
      handleBulkCallToActionClick={handleBulkImportButtonClick}
    >
      <DepartmentWrapper>
        <SuccessModal
          open={isSuccessModalOpen}
          handleClickOutside={handlePersistModal}
          className={"delete-department-success-modal"}
          title={"Success"}
          message={`Department has been ${flag === "delete" ? "deleted" : "added"} successfully`}
          callToAction={"Close"}
          handleCallToActionClick={handleCloseSuccessModal}
        />
        <AddDepartmentModal
          handleActionItemClick={handleActionItemClick}
          anchorElement={bulkImportAnchorElement}
        />
        <DepartmentBulkUploadModal
          width={"40%"}
          height={"auto"}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
        />

        <FiltersCard aria-label="Advanced filters">
          <p className="filter-title">
            <FontAwesomeIcon icon={faFilter} />
            Advanced Filters
          </p>
          <div className="filters-grid">
            <BaseFieldSet>
              <Label>Department Name</Label>
              <BaseInput
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name..."
              />
            </BaseFieldSet>
            <BaseFieldSet>
              <Label>Directory Range</Label>
              <BaseSelect
                value={alphabetFilter}
                onChange={(e) => setAlphabetFilter(e.target.value)}
              >
                <option value="all">All Departments</option>
                <option value="a-m">A - M</option>
                <option value="n-z">N - Z</option>
              </BaseSelect>
            </BaseFieldSet>
            <BaseButton
              type="button"
              className="reset-filter-btn"
              onClick={() => {
                setSearchTerm("");
                setAlphabetFilter("all");
              }}
            >
              Reset Filters
            </BaseButton>
          </div>
        </FiltersCard>

        <DepartmentTableCard>
          <table className="department-grid">
            <thead>
              <tr>
                <th>Department</th>
                <th>Directory Range</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDepartments.map((department, index) => {
                const departmentName =
                  department?.name || "Untitled Department";
                const normalizedName = departmentName.trim();
                const firstCharacter = normalizedName.charAt(0).toUpperCase();
                const directoryGroup =
                  firstCharacter >= "A" && firstCharacter <= "M"
                    ? "A - M"
                    : "N - Z";

                return (
                  <tr key={department?.departmentId || index}>
                    <td className="department-cell">
                      <span className="avatar">{firstCharacter || "D"}</span>
                      <div className="department-meta">
                        <span className="department-name">
                          {departmentName}
                        </span>
                        <span className="department-email">
                          Department record
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`department-badge ${
                          directoryGroup === "A - M" ? "green" : "blue"
                        }`}
                      >
                        {directoryGroup}
                      </span>
                    </td>
                    <td
                      className="action-cell"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDepartmentId((currentId) =>
                          currentId === department?.departmentId
                            ? null
                            : department?.departmentId,
                        );
                      }}
                    >
                      <span className="action-trigger">
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </span>
                      {activeDepartmentId === department?.departmentId && (
                        <ul ref={dropdownRef} className="action-dropdown">
                          <li
                            onClick={(e) =>
                              handleDepartmentAction(
                                e,
                                department?.departmentId,
                                "edit",
                              )
                            }
                          >
                            Edit Department
                          </li>
                          <li
                            onClick={(e) =>
                              handleDepartmentAction(
                                e,
                                department?.departmentId,
                                "delete",
                              )
                            }
                          >
                            Delete Department
                          </li>
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredDepartments.length === 0 && (
            <div className="empty-state">
              <h3>No departments found</h3>
              <p>
                Try a different search term, clear the filter, or add a new
                department to get started.
              </p>
            </div>
          )}
          <div className="table-footer">
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalFilteredDepartments}{" "}
              department
              {totalFilteredDepartments !== 1 ? "s" : ""}
            </span>
            <div className="pagination" aria-hidden="true">
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                &lt;
              </button>
              {visiblePageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`page-chip ${
                    currentPage === pageNumber ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                className="page-chip"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                &gt;
              </button>
            </div>
          </div>
        </DepartmentTableCard>

        <div>{error && <P className="error-text">{error}</P>}</div>
      </DepartmentWrapper>
    </Layout>
  );
};
